import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { useRillContext } from "../../../rill/RillReact/share/base";
import HintTooltip from "./HintTooltip";
import { buildFacilitySchedulingFiles } from "./scheduling-bookable-fetch";
import {
  BOOKING_CUTOFF_OPTIONS,
  normalizeBookingCutoff,
} from "./scheduling-limit-period";

const USER_TYPES = [
  { id: "push", label: "PUSH (Programs only)" },
  { id: "pull", label: "PULL (1:1 Coaching)" },
];

const DURATION_OPTIONS = [15, 30, 45, 60, 75, 90, 105, 120];

const WEEK_DAYS = [
  { key: "mon", day_of_week: 0, label: "Monday" },
  { key: "tue", day_of_week: 1, label: "Tuesday" },
  { key: "wed", day_of_week: 2, label: "Wednesday" },
  { key: "thu", day_of_week: 3, label: "Thursday" },
  { key: "fri", day_of_week: 4, label: "Friday" },
  { key: "sat", day_of_week: 5, label: "Saturday" },
  { key: "sun", day_of_week: 6, label: "Sunday" },
];

/** Matches POST /scheduling/facilities JSON examples (same default as class/PT modals until a TZ picker exists). */
const FACILITY_DEFAULT_TIMEZONE = "America/New_York";

function newSlot() {
  return {
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
    start: "11:30",
    end: "23:30",
  };
}

function emptyDayRanges() {
  return WEEK_DAYS.reduce((acc, d) => {
    acc[d.key] = [newSlot()];
    return acc;
  }, {});
}

/** Same as PT modal — browsers may emit `HH:mm:ss` from native time inputs. */
function formatTimeHHmm(hhmm) {
  if (!hhmm || typeof hhmm !== "string") return "";
  const m = hhmm.trim().match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?$/);
  if (!m) return "";
  const h = Math.min(23, Math.max(0, Number(m[1])));
  return `${String(h).padStart(2, "0")}:${m[2]}`;
}

function cloneSlotsForPaste(slots) {
  const t = Date.now();
  return (slots || []).map((s, i) => ({
    id: `${t}-${i}-${Math.random().toString(36).slice(2, 9)}`,
    start: s.start,
    end: s.end,
  }));
}

function hhmmForTimeInputFacility(s) {
  if (!s || typeof s !== "string") return "";
  const m = s.trim().match(/^(\d{1,2}):(\d{2})/);
  if (!m) return "";
  const h = Math.min(23, Math.max(0, Number(m[1])));
  return `${String(h).padStart(2, "0")}:${m[2]}`;
}

function galleryUrlsFromFacilityRecord(r) {
  if (!r || typeof r !== "object") return [];
  let imgs =
    Array.isArray(r.facility_image_urls) && r.facility_image_urls.length > 0
      ? r.facility_image_urls
      : Array.isArray(r.image_urls) && r.image_urls.length > 0
        ? r.image_urls
        : Array.isArray(r.images) && r.images.length > 0
          ? r.images
          : [];
  imgs = imgs.filter((u) => typeof u === "string" && u.trim());
  if (
    imgs.length === 0 &&
    typeof r.facility_image_url === "string" &&
    r.facility_image_url
  ) {
    imgs = [r.facility_image_url];
  }
  return imgs;
}

function snapshotKeyForFacilityRecord(r) {
  if (!r || typeof r !== "object") return "";
  const id = String(r._id ?? r.id ?? "");
  const wins = r.windows;
  const thumb = typeof r.thumbnail_url === "string" ? r.thumbnail_url : "";
  const galleryKey = galleryUrlsFromFacilityRecord(r).join("|");
  if (!Array.isArray(wins)) return `${id}:${thumb}:${galleryKey}`;
  const wk = wins
    .map((w) => {
      const dow = w.day_of_week ?? w.dayOfWeek ?? "";
      const st = String(w.start_time ?? w.startTime ?? "");
      const et = String(w.end_time ?? w.endTime ?? "");
      return `${dow}:${st}:${et}`;
    })
    .sort()
    .join("|");
  return `${id}:${wk}:${thumb}:${galleryKey}`;
}

function windowsToDayRangesFacility(windows) {
  const base = WEEK_DAYS.reduce((acc, d) => {
    acc[d.key] = [];
    return acc;
  }, {});
  if (!Array.isArray(windows) || windows.length === 0) return base;
  const byDay = {};
  windows.forEach((w, wi) => {
    const dow = w.day_of_week ?? w.dayOfWeek;
    if (dow == null || dow < 0 || dow > 6) return;
    const day = WEEK_DAYS.find((d) => d.day_of_week === dow);
    if (!day) return;
    const key = day.key;
    if (!byDay[key]) byDay[key] = [];
    const st = hhmmForTimeInputFacility(w.start_time ?? w.startTime ?? "");
    const et = hhmmForTimeInputFacility(w.end_time ?? w.endTime ?? "");
    if (!st && !et) return;
    byDay[key].push({
      id: `fc-${key}-${wi}-${st}-${et}`,
      start: st,
      end: et,
    });
  });
  const next = { ...base };
  for (const k of Object.keys(next)) {
    if (byDay[k]?.length) next[k] = byDay[k];
  }
  return next;
}

const CreateFacilityModal = ({
  isOpen,
  onClose,
  onConfirm,
  initialRecord,
  initialScrollSection = null,
  hasPressScheduling = false,
}) => {
  const { locals } = useRillContext();
  const activeUser = locals?.activeUser;
  const coachId = activeUser?._id != null ? String(activeUser._id) : "";

  const thumbInputRef = useRef(null);
  const galleryInputRef = useRef(null);

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [durationMinutes, setDurationMinutes] = useState(30);
  const [capacity, setCapacity] = useState(10);
  const [bookingCutoff, setBookingCutoff] = useState("");
  const [location, setLocation] = useState("");
  const [dayRanges, setDayRanges] = useState(emptyDayRanges);
  const [copiedSlots, setCopiedSlots] = useState(null);

  const [thumbnailFile, setThumbnailFile] = useState(null);
  const [thumbnailPreview, setThumbnailPreview] = useState(null);
  const [galleryItems, setGalleryItems] = useState([]);

  const [pressEnabled, setPressEnabled] = useState(false);
  const [accessMode, setAccessMode] = useState("user_type");
  const [userTypes, setUserTypes] = useState({ push: true, pull: false });
  const [plans, setPlans] = useState([]);
  const [memberships, setMemberships] = useState({});

  const [submitError, setSubmitError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const accessSectionRef = useRef(null);

  const selectAccessMode = (mode) => {
    setAccessMode(mode);
    if (mode === "user_type") {
      setMemberships(Object.fromEntries(plans.map(({ id }) => [id, false])));
    } else {
      setUserTypes({ push: false, pull: false });
    }
  };

  const showPressToggle =
    hasPressScheduling || Boolean(initialRecord?.is_restricted);

  const restrictedNoSelection =
    pressEnabled &&
    (accessMode === "user_type"
      ? !USER_TYPES.some(({ id }) => userTypes[id])
      : !Object.keys(memberships).some((id) => memberships[id]));

  const resetForm = useCallback(() => {
    setName("");
    setDescription("");
    setDurationMinutes(30);
    setCapacity(10);
    setBookingCutoff("");
    setLocation("");
    setDayRanges(emptyDayRanges());
    setCopiedSlots(null);
    setThumbnailFile(null);
    setThumbnailPreview(null);
    setGalleryItems([]);
    setPressEnabled(false);
    setAccessMode("user_type");
    setUserTypes({ push: true, pull: false });
    setMemberships(Object.fromEntries(plans.map(({ id }) => [id, false])));
    setSubmitError(null);
  }, [plans]);

  const applyFacilityRecord = useCallback(
    (r) => {
      if (!r || typeof r !== "object") return;
      setName(r.name ?? r.title ?? "");
      setDescription(r.description ?? "");
      setDurationMinutes(Number(r.duration_minutes) || 30);
      setCapacity(Number(r.capacity) > 0 ? Number(r.capacity) : 10);
      setBookingCutoff(
        r.booking_cutoff == null ? "" : String(r.booking_cutoff)
      );
      setLocation((r.location ?? "").trim());
      setPressEnabled(Boolean(r.is_restricted));
      const aut = Array.isArray(r.allowed_user_types)
        ? r.allowed_user_types
        : [];
      const amp = Array.isArray(r.allowed_membership_plans)
        ? r.allowed_membership_plans
        : [];

      const byPlans = amp.length > 0;
      setUserTypes({
        push: !byPlans && aut.includes("push"),
        pull: !byPlans && aut.includes("pull"),
      });
      setMemberships(
        Object.fromEntries(plans.map(({ id }) => [id, amp.includes(id)]))
      );
      setAccessMode(byPlans ? "membership" : "user_type");
      const win = r.windows;
      if (Array.isArray(win) && win.length > 0) {
        const copy = win.map((w) => ({ ...w }));
        setDayRanges(windowsToDayRangesFacility(copy));
      } else {
        setDayRanges(emptyDayRanges());
      }
      setCopiedSlots(null);
      setThumbnailFile(null);
      setThumbnailPreview(
        typeof r.thumbnail_url === "string" && r.thumbnail_url
          ? r.thumbnail_url
          : null
      );
      const gUrls = galleryUrlsFromFacilityRecord(r);
      setGalleryItems(
        gUrls.map((url, i) => ({
          id: `existing-${i}-${url.slice(-32)}`,
          url,
          preview: url,
        }))
      );
      setSubmitError(null);
    },
    [plans]
  );

  useEffect(() => {
    let cancelled = false;
    fetch("/actions/coach-settings/membership-plans", {
      credentials: "same-origin",
    })
      .then((res) => (res.ok ? res.json() : []))
      .then((data) => {
        if (cancelled) return;
        setPlans(Array.isArray(data) ? data : []);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!isOpen || initialRecord || plans.length === 0) return;
    setMemberships((prev) =>
      Object.keys(prev).length === 0
        ? Object.fromEntries(plans.map(({ id }) => [id, false]))
        : prev
    );
  }, [isOpen, initialRecord, plans]);

  const lastAppliedSnapshotRef = useRef(null);
  const lastAppliedPlansRef = useRef(null);

  useEffect(() => {
    if (!isOpen) {
      resetForm();
      lastAppliedSnapshotRef.current = null;
      lastAppliedPlansRef.current = null;
      return;
    }
    if (!initialRecord) {
      if (lastAppliedSnapshotRef.current !== "create") {
        resetForm();
        lastAppliedSnapshotRef.current = "create";
        lastAppliedPlansRef.current = null;
      }
      return;
    }
    const snap = snapshotKeyForFacilityRecord(initialRecord);
    if (
      lastAppliedSnapshotRef.current === snap &&
      lastAppliedPlansRef.current === plans
    ) {
      return;
    }
    lastAppliedSnapshotRef.current = snap;
    lastAppliedPlansRef.current = plans;
    applyFacilityRecord(initialRecord);
  }, [isOpen, initialRecord, applyFacilityRecord, resetForm, plans]);

  useLayoutEffect(() => {
    if (!isOpen || initialScrollSection !== "access_permissions") return;
    let raf2 = 0;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => {
        accessSectionRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      });
    });
    return () => {
      cancelAnimationFrame(raf1);
      if (raf2) cancelAnimationFrame(raf2);
    };
  }, [isOpen, initialScrollSection]);

  const handleClose = (e) => {
    e?.preventDefault?.();
    onClose();
  };

  const updateSlot = (dayKey, index, field, value) => {
    setDayRanges((prev) => {
      const next = { ...prev };
      const list = [...(next[dayKey] || [])];
      list[index] = { ...list[index], [field]: value };
      next[dayKey] = list;
      return next;
    });
  };

  const addSlot = (dayKey) => {
    setDayRanges((prev) => ({
      ...prev,
      [dayKey]: [...(prev[dayKey] || []), newSlot()],
    }));
  };

  const removeSlot = (dayKey, index) => {
    setDayRanges((prev) => {
      const list = [...(prev[dayKey] || [])];
      if (list.length <= 1) {
        return { ...prev, [dayKey]: [] };
      }
      list.splice(index, 1);
      return { ...prev, [dayKey]: list };
    });
  };

  const copyDaySchedule = (dayKey) => {
    const slots = dayRanges[dayKey] || [];
    setCopiedSlots(slots.map(({ start, end }) => ({ start, end })));
  };

  const pasteDaySchedule = (dayKey) => {
    if (!copiedSlots?.length) return;
    setDayRanges((prev) => ({
      ...prev,
      [dayKey]: cloneSlotsForPaste(copiedSlots),
    }));
  };

  const toggleUserType = (id) => {
    setUserTypes((prev) => ({ ...prev, [id]: !prev[id] }));
  };
  const toggleMembership = (id) => {
    setMemberships((prev) => ({ ...prev, [id]: !prev[id] }));
  };
  const allMembershipsChecked =
    plans.length > 0 && plans.every(({ id }) => memberships[id]);
  const setAllMemberships = (checked) => {
    const next = {};
    for (const { id } of plans) next[id] = checked;
    setMemberships(next);
  };

  const onThumbnailPick = (e) => {
    const file = e.target.files?.[0];
    if (!file || !file.type.startsWith("image/")) return;
    setThumbnailFile(file);
    setThumbnailPreview((prev) => {
      if (prev?.startsWith?.("blob:")) URL.revokeObjectURL(prev);
      return URL.createObjectURL(file);
    });
    e.target.value = "";
  };

  const onGalleryPick = (e) => {
    const files = Array.from(e.target.files || []).filter((f) =>
      f.type.startsWith("image/")
    );
    if (!files.length) return;
    setGalleryItems((prev) => {
      const next = [...prev];
      for (const file of files) {
        next.push({
          id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
          file,
          preview: URL.createObjectURL(file),
        });
      }
      return next;
    });
    e.target.value = "";
  };

  const removeGalleryItem = (id) => {
    setGalleryItems((prev) => {
      const item = prev.find((x) => x.id === id);
      if (item?.preview?.startsWith?.("blob:")) {
        URL.revokeObjectURL(item.preview);
      }
      return prev.filter((x) => x.id !== id);
    });
  };

  /**
   * Weekly windows as a JSON array for the API (multiple rows per day allowed).
   * Same shape as PT: `{ day_of_week, start_time, end_time }` with numeric DOW and `HH:mm` strings.
   */
  const buildFacilityWindowsForApi = () => {
    const windows = [];
    for (const { key, day_of_week } of WEEK_DAYS) {
      const ranges = dayRanges[key] || [];
      for (const r of ranges) {
        if (!r.start || !r.end) continue;
        const start_time = formatTimeHHmm(r.start);
        const end_time = formatTimeHHmm(r.end);
        if (start_time && end_time) {
          windows.push({
            day_of_week: Number(day_of_week),
            start_time: String(start_time),
            end_time: String(end_time),
          });
        }
      }
    }
    return windows;
  };

  const canSubmit =
    name.trim().length > 0 &&
    coachId &&
    Number(durationMinutes) > 0 &&
    Number(capacity) > 0 &&
    buildFacilityWindowsForApi().length > 0;

  const handleSave = async (e) => {
    e?.preventDefault?.();
    if (restrictedNoSelection) {
      setSubmitError(
        accessMode === "user_type"
          ? "Select at least one user type when Press Users is enabled."
          : "Select at least one membership plan when Press Users is enabled."
      );
      return;
    }
    if (typeof onConfirm !== "function") {
      handleClose(e);
      return;
    }
    if (!canSubmit) {
      setSubmitError(
        !coachId
          ? "You must be signed in as a coach to create a facility."
          : "Add a name, capacity (at least 1), and at least one complete time window (start and end)."
      );
      return;
    }
    setSubmitError(null);
    setIsSubmitting(true);
    try {
      const windows = buildFacilityWindowsForApi();
      const existingGalleryUrls = galleryItems
        .map((g) => g.url)
        .filter((u) => typeof u === "string" && u.trim());

      let thumbnail_url = "";
      if (thumbnailFile) {
        thumbnail_url = "";
      } else if (
        typeof thumbnailPreview === "string" &&
        thumbnailPreview &&
        !thumbnailPreview.startsWith("blob:")
      ) {
        thumbnail_url = thumbnailPreview;
      }

      const firstG = galleryItems[0];
      const facility_image_url =
        typeof firstG?.url === "string" && firstG.url ? firstG.url : "";

      const restricted = pressEnabled;
      const allowed_user_types =
        restricted && accessMode === "user_type"
          ? USER_TYPES.filter(({ id }) => userTypes[id]).map(({ id }) => id)
          : [];
      const allowed_membership_plans =
        restricted && accessMode === "membership"
          ? Object.keys(memberships).filter((id) => memberships[id])
          : [];

      const payload = {
        name: name.trim(),
        description: description.trim(),
        duration_minutes: Number(durationMinutes) || 0,
        capacity: Number(capacity) || 0,
        booking_cutoff: normalizeBookingCutoff(bookingCutoff),
        location: location.trim(),
        schedule_type: "custom",
        timezone: FACILITY_DEFAULT_TIMEZONE,
        thumbnail_url,
        facility_image_url,
        is_restricted: restricted,
        windows,
        facility_image_urls: existingGalleryUrls,
        allowed_user_types: restricted ? allowed_user_types : [],
        allowed_membership_plans: restricted ? allowed_membership_plans : [],
      };

      const files = buildFacilitySchedulingFiles(galleryItems, thumbnailFile);

      await Promise.resolve(onConfirm(payload, { files }));
      handleClose(e);
    } catch (err) {
      setSubmitError(
        err?.message ||
          (initialRecord?._id || initialRecord?.id
            ? "Failed to save facility"
            : "Failed to create facility")
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const editId = initialRecord?._id ?? initialRecord?.id;
  const isEdit = Boolean(editId);

  if (!isOpen) return null;

  return (
    <div
      className="Modal-Box create-class-modal create-facility-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="create-facility-modal-title"
    >
      <button
        type="button"
        className="overlay"
        onClick={handleClose}
        aria-label="Close modal"
      />
      <div className="box">
        <header>
          <h2 id="create-facility-modal-title">
            {isEdit ? "Edit facility" : "Create Facilities"}
          </h2>
          <button
            type="button"
            onClick={handleClose}
            className="close"
            data-button-type="tertiary"
            data-icon-only="true"
            aria-label="Close"
          >
            <img src="/img/new-icons/red-cross.svg" alt="" />
          </button>
        </header>
        <div className="body">
          <div className="create-class-modal-cols">
            <div className="create-class-modal-section-head">
              <h3 className="create-class-modal-section-title">
                Basic Details
              </h3>
              <p className="create-class-modal-section-desc">
                Define the core settings for this Facility.
              </p>
            </div>
            <div className="create-class-modal-form">
              <div className="form-group">
                <label htmlFor="create-facility-name">Facility Name</label>
                <input
                  id="create-facility-name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g. sauna, swimming pool"
                  aria-label="Facility name"
                />
              </div>
              <div className="form-group">
                <label htmlFor="create-facility-description">Description</label>
                <textarea
                  id="create-facility-description"
                  rows={3}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Add details about the Facility, what to expect, requirements, or anything members should know."
                  aria-label="Description"
                />
              </div>
              <div className="create-facility-two-cols">
                <div className="create-facility-col">
                  <div className="form-group">
                    <label
                      htmlFor="create-facility-capacity"
                      className="create-class-modal-label-with-hint"
                    >
                      Capacity
                      <HintTooltip
                        label="Capacity help"
                        message="Maximum seats for this facility"
                      />
                    </label>
                    <input
                      id="create-facility-capacity"
                      type="number"
                      min={1}
                      max={9999}
                      step={1}
                      inputMode="numeric"
                      value={capacity === 0 ? "" : capacity}
                      onChange={(e) => {
                        const v = e.target.value;
                        if (v === "") {
                          setCapacity(0);
                          return;
                        }
                        const n = parseInt(v, 10);
                        if (!Number.isNaN(n))
                          setCapacity(Math.min(9999, Math.max(1, n)));
                      }}
                      aria-label="Capacity in seats"
                    />
                  </div>
                  <div className="form-group">
                    <button
                      type="button"
                      className="create-facility-upload-zone"
                      onClick={() => thumbInputRef.current?.click()}
                    >
                      {thumbnailPreview ? (
                        <img
                          src={thumbnailPreview}
                          alt=""
                          className="create-facility-thumb-preview"
                        />
                      ) : (
                        <>
                          <span className="create-facility-upload-title">
                            Upload Thumbnail
                          </span>
                          <span className="create-facility-upload-hint">
                            Recommend size 16:9
                          </span>
                        </>
                      )}
                    </button>
                    <input
                      ref={thumbInputRef}
                      id="create-facility-thumb"
                      type="file"
                      accept="image/*"
                      className="create-facility-file-input"
                      onChange={onThumbnailPick}
                      aria-label="Upload thumbnail image"
                    />
                  </div>
                </div>
                <div className="create-facility-col">
                  <div className="form-group">
                    <label htmlFor="create-facility-duration">Duration</label>
                    <div className="create-class-modal-select-wrapper">
                      <select
                        id="create-facility-duration"
                        value={durationMinutes}
                        onChange={(e) =>
                          setDurationMinutes(Number(e.target.value))
                        }
                        aria-label="Session duration"
                      >
                        {DURATION_OPTIONS.map((n) => (
                          <option key={n} value={n}>
                            {n} min
                          </option>
                        ))}
                      </select>
                      <img
                        src="/img/new-icons/arrow-down.svg"
                        alt=""
                        className="create-class-modal-select-chevron"
                        aria-hidden="true"
                      />
                    </div>
                  </div>
                  <div className="form-group">
                    <label htmlFor="create-facility-location">Location</label>
                    <input
                      id="create-facility-location"
                      type="text"
                      value={location}
                      onChange={(e) => setLocation(e.target.value)}
                      onBlur={() => setLocation((v) => v.trim())}
                      placeholder="E.g. Training room 1st floor"
                      title={location || undefined}
                      aria-label="Location"
                    />
                  </div>
                  <div className="form-group">
                    <label
                      htmlFor="create-facility-booking-cutoff"
                      className="create-class-modal-label-with-hint"
                    >
                      Booking cutoff
                      <HintTooltip
                        label="Booking cutoff help"
                        message="Blocks new bookings this amount of time before the event starts."
                      />
                    </label>
                    <div className="create-class-modal-select-wrapper">
                      <select
                        id="create-facility-booking-cutoff"
                        value={bookingCutoff}
                        onChange={(e) => setBookingCutoff(e.target.value)}
                        aria-label="Booking cutoff in hours"
                      >
                        {BOOKING_CUTOFF_OPTIONS.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                      <img
                        src="/img/new-icons/arrow-down.svg"
                        alt=""
                        className="create-class-modal-select-chevron"
                        aria-hidden="true"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <hr className="create-class-modal-separator" />

          <div className="create-class-modal-cols create-pt-session-availability-cols">
            <div className="create-class-modal-section-head">
              <h3 className="create-class-modal-section-title">Availability</h3>
              <p className="create-class-modal-section-desc">
                <strong>(Weekly Recurring)</strong>
                <br />
                Set the days and time windows when this facility can be booked.
              </p>
              <p className="create-pt-session-schedule-label">
                Schedule:{" "}
                <span className="create-pt-session-schedule-value">Custom</span>
              </p>
            </div>
            <div className="create-class-modal-form create-pt-session-availability-form">
              <ul className="create-pt-session-week-list">
                {WEEK_DAYS.flatMap(({ key, label }) => {
                  const slots = dayRanges[key] || [];
                  if (slots.length === 0) {
                    return [
                      <li
                        key={`${key}-unavailable`}
                        className="create-pt-session-day-row"
                      >
                        <span className="create-pt-session-day-cell">
                          {label}
                        </span>
                        <span className="create-pt-session-unavailable-label">
                          Unavailable
                        </span>
                        <div className="create-pt-session-day-actions">
                          <button
                            type="button"
                            className="create-pt-session-icon-btn"
                            onClick={() => addSlot(key)}
                            aria-label={`Add time range on ${label}`}
                            title="Add time range"
                          >
                            <img
                              src="/img/new-icons/add-circle-outline.svg"
                              alt=""
                              aria-hidden="true"
                              className="create-pt-session-add-icon"
                            />
                          </button>
                        </div>
                      </li>,
                    ];
                  }
                  return slots.map((slot, index) => (
                    <li
                      key={`${key}-${slot.id}`}
                      className={
                        "create-pt-session-day-row" +
                        (index > 0
                          ? " create-pt-session-day-row--continued"
                          : "")
                      }
                    >
                      {index === 0 ? (
                        <span className="create-pt-session-day-cell">
                          {label}
                        </span>
                      ) : (
                        <span
                          className="create-pt-session-day-cell create-pt-session-day-cell--empty"
                          aria-hidden="true"
                        />
                      )}
                      <div className="create-pt-session-range-pill">
                        <input
                          type="time"
                          value={slot.start}
                          onChange={(e) =>
                            updateSlot(key, index, "start", e.target.value)
                          }
                          aria-label={`${label} start time, range ${index + 1}`}
                          className="create-pt-session-time-input"
                        />
                        <span
                          className="create-pt-session-range-sep"
                          aria-hidden="true"
                        >
                          -
                        </span>
                        <input
                          type="time"
                          value={slot.end}
                          onChange={(e) =>
                            updateSlot(key, index, "end", e.target.value)
                          }
                          aria-label={`${label} end time, range ${index + 1}`}
                          className="create-pt-session-time-input"
                        />
                      </div>
                      <div className="create-pt-session-day-actions">
                        <button
                          type="button"
                          className="create-pt-session-icon-btn"
                          onClick={() => removeSlot(key, index)}
                          aria-label={`Remove time range ${index + 1} on ${label}`}
                          title="Remove this time range"
                        >
                          <img
                            src="/img/new-icons/cross-light.svg"
                            alt=""
                            aria-hidden="true"
                            className="create-pt-session-icon-cross"
                          />
                        </button>
                        {index === 0 && (
                          <>
                            <button
                              type="button"
                              className="create-pt-session-icon-btn"
                              onClick={() => addSlot(key)}
                              aria-label={`Add time range on ${label}`}
                              title="Add time range"
                            >
                              <img
                                src="/img/new-icons/add-circle-outline.svg"
                                alt=""
                                aria-hidden="true"
                                className="create-pt-session-add-icon"
                              />
                            </button>
                            <button
                              type="button"
                              className="create-pt-session-icon-btn"
                              onClick={() => copyDaySchedule(key)}
                              aria-label={`Copy ${label} schedule`}
                              title="Copy this day’s time windows"
                            >
                              <img
                                src="/img/new-icons/duplicate.svg"
                                alt=""
                                aria-hidden="true"
                                className="create-pt-session-duplicate-icon"
                              />
                            </button>
                            {copiedSlots && (
                              <button
                                type="button"
                                className="create-pt-session-icon-btn"
                                onClick={() => pasteDaySchedule(key)}
                                aria-label={`Paste schedule into ${label}`}
                                title="Paste copied schedule"
                              >
                                <img
                                  src="/img/new-icons/clipboard-notes.svg"
                                  alt=""
                                  aria-hidden="true"
                                />
                              </button>
                            )}
                          </>
                        )}
                      </div>
                    </li>
                  ));
                })}
              </ul>
            </div>
          </div>

          <hr className="create-class-modal-separator" />

          <div className="create-class-modal-cols">
            <div className="create-class-modal-section-head">
              <h3 className="create-class-modal-section-title">
                Upload Facility Images
              </h3>
              <p className="create-class-modal-section-desc">
                Upload high-quality images to showcase the space.
              </p>
            </div>
            <div className="create-class-modal-form">
              {galleryItems.length === 0 ? (
                <button
                  type="button"
                  className="create-facility-image-dropzone"
                  onClick={() => galleryInputRef.current?.click()}
                >
                  <span className="create-facility-dropzone-plus">+</span>
                  <span className="create-facility-dropzone-link">
                    Add or drag &amp; drop image
                  </span>
                  <span className="create-facility-dropzone-hint">
                    Recommend size 16:9
                  </span>
                </button>
              ) : (
                <div className="create-facility-images-gallery">
                  {galleryItems.map((g) => (
                    <div key={g.id} className="create-facility-gallery-item">
                      <img src={g.preview} alt="" />
                      <button
                        type="button"
                        className="create-facility-gallery-remove"
                        onClick={() => removeGalleryItem(g.id)}
                        aria-label="Remove image"
                      >
                        <img src="/img/new-icons/close-circle.svg" alt="" />
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    className="create-facility-add-tile"
                    onClick={() => galleryInputRef.current?.click()}
                  >
                    <span className="create-facility-dropzone-plus">+</span>
                    <span className="create-facility-dropzone-link">
                      Add image
                    </span>
                  </button>
                </div>
              )}
              <input
                ref={galleryInputRef}
                type="file"
                accept="image/*"
                multiple
                className="create-facility-file-input"
                onChange={onGalleryPick}
                aria-label="Upload facility images"
              />
            </div>
          </div>

          {showPressToggle && (
            <>
              <hr className="create-class-modal-separator" />

              <div
                ref={accessSectionRef}
                id="scheduling-edit-modal-access-permissions-facility"
                className="create-class-modal-cols create-class-modal-access-booking"
              >
                <div className="create-class-modal-section-head">
                  <h3 className="create-class-modal-section-title">
                    Access &amp; Permissions
                  </h3>
                  <p className="create-class-modal-section-desc">
                    Control who can view and book this session.
                  </p>
                </div>
                <div className="create-class-modal-form scheduling-access-options-wrapper">
                  <div className="scheduling-access-options">
                    <div className="scheduling-access-row scheduling-access-row-switch scheduling-access-row-press">
                      <input
                        type="checkbox"
                        role="switch"
                        aria-label="Press users access"
                        aria-checked={pressEnabled}
                        checked={pressEnabled}
                        onChange={() => setPressEnabled(!pressEnabled)}
                      />
                      <span className="scheduling-access-row-text">
                        <span className="scheduling-access-row-title">
                          PRESS USERS
                        </span>
                        <span className="scheduling-access-row-desc">
                          Members without access will not see this facility.
                        </span>
                      </span>
                    </div>
                    {pressEnabled && (
                      <>
                        <div
                          className="scheduling-access-mode"
                          role="radiogroup"
                          aria-label="Restrict access by"
                        >
                          <label className="radio-input">
                            <input
                              type="radio"
                              name="create-facility-access-mode"
                              checked={accessMode === "user_type"}
                              onChange={() => selectAccessMode("user_type")}
                            />
                            <span className="radio" aria-hidden="true" />
                            <span className="text">By user type</span>
                          </label>
                          <label className="radio-input">
                            <input
                              type="radio"
                              name="create-facility-access-mode"
                              checked={accessMode === "membership"}
                              onChange={() => selectAccessMode("membership")}
                            />
                            <span className="radio" aria-hidden="true" />
                            <span className="text">By membership plans</span>
                          </label>
                        </div>
                        <div className="scheduling-access-subsections">
                          <section
                            className={`scheduling-access-subsection${
                              accessMode !== "user_type" ? " is-disabled" : ""
                            }`}
                            aria-disabled={accessMode !== "user_type"}
                          >
                            <h3 className="scheduling-access-subsection-title">
                              User type
                            </h3>
                            <div className="scheduling-access-subsection-body">
                              {USER_TYPES.map(({ id, label }) => (
                                <label key={id} className="checkbox-input">
                                  <input
                                    type="checkbox"
                                    checked={userTypes[id]}
                                    disabled={accessMode !== "user_type"}
                                    onChange={() => toggleUserType(id)}
                                  />
                                  <span
                                    className="checkbox"
                                    aria-hidden="true"
                                  />
                                  <span className="text">{label}</span>
                                </label>
                              ))}
                            </div>
                          </section>
                          <section
                            className={`scheduling-access-subsection${
                              accessMode !== "membership" ? " is-disabled" : ""
                            }`}
                            aria-disabled={accessMode !== "membership"}
                          >
                            <div className="scheduling-access-subsection-header">
                              <h3 className="scheduling-access-subsection-title">
                                Memberships
                              </h3>
                              <button
                                type="button"
                                className="scheduling-settings-all"
                                disabled={accessMode !== "membership"}
                                onClick={() =>
                                  setAllMemberships(!allMembershipsChecked)
                                }
                                aria-pressed={allMembershipsChecked}
                              >
                                <img
                                  src={
                                    allMembershipsChecked
                                      ? "/img/new-icons/checked-square.svg"
                                      : "/img/new-icons/unchecked-square.svg"
                                  }
                                  alt=""
                                  aria-hidden="true"
                                />
                                <span>All</span>
                              </button>
                            </div>
                            <div className="scheduling-access-subsection-body">
                              {plans.map(({ id, label }) => (
                                <label key={id} className="checkbox-input">
                                  <input
                                    type="checkbox"
                                    checked={memberships[id] ?? false}
                                    disabled={accessMode !== "membership"}
                                    onChange={() => toggleMembership(id)}
                                  />
                                  <span
                                    className="checkbox"
                                    aria-hidden="true"
                                  />
                                  <span className="text" title={label}>
                                    {label}
                                  </span>
                                </label>
                              ))}
                            </div>
                          </section>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </>
          )}

          {submitError && (
            <p className="create-class-modal-error" role="alert">
              {submitError}
            </p>
          )}
          <footer className="scheduling-classes-modal-footer">
            <button
              type="button"
              data-button-type="outline"
              onClick={handleClose}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="button"
              data-button-type="primary"
              onClick={handleSave}
              disabled={isSubmitting}
            >
              {isSubmitting ? "Saving…" : isEdit ? "Save changes" : "Save"}
            </button>
          </footer>
        </div>
      </div>
    </div>
  );
};

export default CreateFacilityModal;
